<script lang="ts">
import { useDesignStore } from '@/stores/designStore.ts'
import WeaponSkillDisplay from './Character/Weapon Attack Builder Remastered/WeaponSkillDisplay.vue'
import { computed, ComputedRef } from 'vue'
import TitleMedallion from './TitleMedallion.vue'
import HeaderTextFancy from './HeaderTextFancy.vue'
import AbilityDisplayMedallion from './AbilityDisplayMedallion.vue'

interface Skill {
  skill: string
  rank: number
  source: string
  id: string
}

export default {
  props: ['specialization'],
  setup(props, context) {
    const designStore = useDesignStore()
    const passive: ComputedRef<any> = computed(() => {
      return Object.values(props.specialization.skills).filter(
        (s: any) => s?.mp_cost == 'Passive'
      )[0]
    })

    const additionalActions = computed(() => {
      return Object.values(props.specialization.skills).filter(
        (s: any) =>
          (s?.skillStats?.type === 'Executes an Action' && !s?.skillStats?.grantsNewStrike) ||
          (s?.skillsStats &&
            s?.skillsStats.modes &&
            s?.skillStats[s?.skillsStats.modes[0]]?.type === 'Executes an Action' &&
            s?.skillStats[s?.skillsStats.modes[0]]?.grantsNewStrike)
      )
    })
    const utilitySkillOptions = computed(() => {
      return Object.values(props.specialization.skills).filter(
        (s: any) =>
          (s?.skillStats.utility ||
            (s?.skillStats.isModal && s?.skillStats[s?.skillStats?.modes[0]]?.utility)) &&
          !(
            s?.skillStats.aoe ||
            s?.skillStats.reachMod ||
            s?.skillStats?.type === 'Executes an Action'
          )
      )
    })

    const mutiattackOptions = computed(() => {
      return Object.values(props.specialization.skills).filter(
        (s: any) =>
          (s?.skillStats.grantsNewStrike ||
            (s.isModal && s[s.skillStats.modes[0]].skillStats.grantsNewStrike)) &&
          s?.mp_cost != 'Passive'
      )
    })

    const damageSkillOptions = computed(() => {
      return Object.values(props.specialization.skills).filter(
        (s: any) =>
          s?.skillStats.damage || (s?.isModal && s?.skillStats[s?.skillStats?.modes[0]]?.damage)
      )
    })
    const aoeStrikeOptions = computed(() => {
      return Object.values(props.specialization.skills).filter(
        (s: any) =>
          (s?.skillStats.aoe ||
            s?.skillStats.reachMod ||
            (s.skillStats.isModal && s.skillStats[s?.skillStats.modes[0]].aoe)) &&
          !s?.skillStats.damage &&
          !s?.skillStats.grantsNewStrike
      )
    })

    return {
      designStore,
      utilitySkillOptions,
      damageSkillOptions,
      passive,
      props,
      aoeStrikeOptions,
      mutiattackOptions,
      additionalActions
    }
  },

  components: { WeaponSkillDisplay, TitleMedallion, HeaderTextFancy, AbilityDisplayMedallion }
}
</script>

<template>
  <div>
    <div v-if="(passive as any).name" :style="{ background: designStore.sidebarBacking }">
      <TitleMedallion title="Passive" :color="designStore.sidebarText"></TitleMedallion>
      <WeaponSkillDisplay
        style="margin-top: -1rem; padding-bottom: 1rem"
        :mpAllocated="(passive as any).mpCost"
        :skillObj="passive"
        :noShadow="true"
        :backDropOverride="designStore.sidebarBacking"
        :colorOverride="designStore.sidebarText"
        :noCost="true"
      ></WeaponSkillDisplay>
    </div>
    <div class="utilityAndDamageContainer">
      <div v-if="utilitySkillOptions.length > 0" style="display: block">
        <HeaderTextFancy title="Utility Skills" style="break-before: avoid"></HeaderTextFancy>
        <WeaponSkillDisplay
          style="border: 2px solid; display: inline-block"
          :style="{ borderColor: designStore.secondaryTheme }"
          v-for="skill in utilitySkillOptions"
          :key="(skill as any).name"
          :mpAllocated="(skill as any).mpCost"
          :skillObj="skill"
          :noShadow="true"
        ></WeaponSkillDisplay>
      </div>
      <div v-if="damageSkillOptions.length > 0" style="display: block">
        <HeaderTextFancy
          title="Damage Skills"
          :style="{
            breakBefore:
              utilitySkillOptions.length >= 3 ||
              (utilitySkillOptions.length === 2 && damageSkillOptions.length > 1)
                ? 'column'
                : 'avoid'
          }"
        ></HeaderTextFancy>

        <WeaponSkillDisplay
          style="border: 2px solid; display: inline-block"
          :style="{ borderColor: designStore.secondaryTheme }"
          v-for="skill in damageSkillOptions"
          :key="(skill as any).name"
          :mpAllocated="(skill as any).mpCost"
          :skillObj="skill"
          :noShadow="true"
        ></WeaponSkillDisplay>
      </div>
      <div v-if="additionalActions.length > 0" style="display: block">
        <HeaderTextFancy
          title="Additional Actions"
          :style="{
            breakBefore:
              damageSkillOptions.length + utilitySkillOptions.length === 3 ? 'column' : 'avoid'
          }"
        ></HeaderTextFancy>

        <WeaponSkillDisplay
          style="border: 2px solid; display: inline-block"
          :style="{ borderColor: designStore.secondaryTheme }"
          v-for="skill in additionalActions"
          :key="(skill as any).name"
          :mpAllocated="(skill as any).mpCost"
          :skillObj="skill"
          :noShadow="true"
        ></WeaponSkillDisplay>
      </div>
      <div v-if="mutiattackOptions.length > 0" style="display: block">
        <HeaderTextFancy
          title="Additional Strikes"
          style="break-before: avoid-column"
          :style="{
            breakBefore:
              damageSkillOptions.length + utilitySkillOptions.length < 5 ? 'column' : 'avoid'
          }"
        ></HeaderTextFancy>
        <WeaponSkillDisplay
          style="border: 2px solid; display: inline-block"
          :style="{ borderColor: designStore.secondaryTheme }"
          v-for="skill in mutiattackOptions"
          :key="(skill as any).name"
          :mpAllocated="(skill as any).mpCost"
          :skillObj="skill"
          :noShadow="true"
        ></WeaponSkillDisplay>
      </div>
      <div v-if="aoeStrikeOptions.length > 0" style="display: block">
        <HeaderTextFancy
          title="Strike Target Modifiers"
          style="break-before: avoid-column"
        ></HeaderTextFancy>
        <WeaponSkillDisplay
          style="border: 2px solid; display: inline-block"
          :style="{ borderColor: designStore.secondaryTheme }"
          v-for="skill in aoeStrikeOptions"
          :key="(skill as any).name"
          :mpAllocated="(skill as any).mpCost"
          :skillObj="skill"
          :noShadow="true"
        ></WeaponSkillDisplay>
      </div>
    </div>
    <AbilityDisplayMedallion :medallion="props.specialization.groupIcon"></AbilityDisplayMedallion>
  </div>
</template>

<style>
.tableClass {
  --bs-table-bg: background-color !important;
  --bs-table-color-type: color !important;
  --bs-emphasis-color: color !important;
}
.utilityAndDamageContainer {
  column-count: 2;
}
@media (max-width: 1000px) {
  .utilityAndDamageContainer {
    column-count: 1;
  }
}
.w-40 {
  width: 40%;
}

.w-40-max {
  width: 30%;
}
@media (max-width: 500px) {
  .w-40 {
    width: 40%;
  }

  .w-40-max {
    width: 40%;
  }
}
.w-10 {
  width: 10%;
}
</style>
